import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { PdApiActions, pdApiActions } from "../../actions/pdApiActions";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";

type StateProps = {
  editedGrandNorm: Api.GrandNorm;
  storedGrandNorm: Api.GrandNorm;
  pdApi: State.PdApi;
  activeUser?: Api.User;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
};

type OwnProps = {
  toggleModal: () => void;
  groupName: Api.GroupName;
};

const grandNormFields = nameofFactory<Api.GrandNorm>();

export type EditGrandNormCommonProps = StateProps & DispatchProps & OwnProps;

export const EditGrandNormCommon: React.FC<EditGrandNormCommonProps> = props => {
  const { editedGrandNorm, pdApi, storedGrandNorm, activeUser, toggleModal, groupName } = props;
  const isNew = !storedGrandNorm;
  // Grand Norms are scoped to a single domain group per menu (e.g. Elevator -> Lift),
  // so the group is always derived from groupName rather than user-selectable.
  const domainGroup = _.values(pdApi.domainGroups).find(
    domainGroup => domainGroup.enumName === groupName
  );

  React.useEffect(() => {
    if (domainGroup && editedGrandNorm.domainGroupId !== domainGroup.enumValue) {
      props.editActions.setEdited({
        ...editedGrandNorm,
        domainGroupId: domainGroup.enumValue,
        domainGroupName: domainGroup.enumName as string
      });
    }
  }, [domainGroup, editedGrandNorm, props.editActions]);

  React.useEffect(() => {
    if (groupName === "Lift" && pdApi.liftFaults === undefined) {
      props.pdApiActions.fetchLiftFaults();
    }
  }, [groupName, pdApi.liftFaults, props.pdApiActions]);

  const isEmptyString = (str: string | undefined): boolean =>
    !(!!str && !!str.trim() && str.trim().length > 0);

  const getGroupName = (domainGroupEnumValue: number): string => {
    const matchedGroup = _.values(pdApi.domainGroups).find(
      domainGroup => domainGroup.enumValue === domainGroupEnumValue
    );
    return matchedGroup ? (matchedGroup.enumName as string) : "";
  };

  const getErrorMessage = (field: string): string => {
    switch (field) {
      case grandNormFields("code"):
        if (editedGrandNorm.code === undefined || editedGrandNorm.code === null) {
          return "The code is mandatory.";
        }
        return "The code must be unique.";
      case grandNormFields("name"):
        if (isEmptyString(editedGrandNorm.name)) {
          return "The name is mandatory.";
        }
        return "The name must be unique within the domain group.";
      case grandNormFields("domainGroupId"):
        if (getGroupName(editedGrandNorm.domainGroupId) === "Unknown") {
          return "Grand Norm cannot be created for Unknown Domain Group.";
        }
        return "The domain group is mandatory.";
      default:
        return "";
    }
  };

  const isFieldValid = (field: string): boolean => {
    const grandNorms = _.values(pdApi.grandNorms ?? []);
    switch (field) {
      case grandNormFields("domainGroupId"):
        return (
          !!editedGrandNorm.domainGroupId &&
          getGroupName(editedGrandNorm.domainGroupId) !== "Unknown"
        );
      case grandNormFields("code"):
        if (
          editedGrandNorm.code === undefined ||
          editedGrandNorm.code === null ||
          !Number.isInteger(editedGrandNorm.code)
        ) {
          return false;
        }
        return !grandNorms.find(
          grandNorm =>
            grandNorm.id !== editedGrandNorm.id &&
            grandNorm.domainGroupId === editedGrandNorm.domainGroupId &&
            grandNorm.code === editedGrandNorm.code
        );
      case grandNormFields("name"):
        if (isEmptyString(editedGrandNorm.name)) {
          return false;
        }
        return !grandNorms.find(
          grandNorm =>
            grandNorm.id !== editedGrandNorm.id &&
            grandNorm.domainGroupId === editedGrandNorm.domainGroupId &&
            grandNorm.name.trim().toLowerCase() === editedGrandNorm.name.trim().toLowerCase()
        );
      default:
        return true;
    }
  };

  const isAllFieldsValid = (): boolean =>
    isFieldValid(grandNormFields("code")) &&
    isFieldValid(grandNormFields("name")) &&
    isFieldValid(grandNormFields("domainGroupId")) &&
    !!editedGrandNorm.domainGroupId;

  const userPermissions = hasWriteAccess(activeUser, groupName);
  const disableDelete =
    groupName === "Lift" &&
    (pdApi.liftFaults === undefined ||
      pdApi.liftFaults.some(fault => fault.grandNormId === editedGrandNorm.id));

  return (
    <Editor
      itemName={"Grand Norm"}
      originalObject={storedGrandNorm}
      editedObject={editedGrandNorm}
      hasWritePermission={userPermissions}
      isValid={isAllFieldsValid()}
      cancel={props.editActions.cancelEditing}
      update={() => props.editActions.update("grandNorms")}
      create={() => props.editActions.add("grandNorms")}
      remove={{
        callback: toggleModal,
        enabledTooltip: "Grand Norm",
        disabled: disableDelete,
        disabledTooltip: ": Item linked to child components"
      }}
    >
      <NumberField
        id="code"
        label="Code"
        value={editedGrandNorm.code}
        isError={!isFieldValid(grandNormFields("code"))}
        errorMessage={getErrorMessage(grandNormFields("code"))}
        readonly={!isNew || !userPermissions}
        onChange={code => props.editActions.setEdited({ ...editedGrandNorm, code })}
      />
      <TextField
        id="name"
        label="Name"
        value={editedGrandNorm.name}
        isError={!isFieldValid(grandNormFields("name"))}
        errorMessage={getErrorMessage(grandNormFields("name"))}
        readonly={!userPermissions}
        onChange={name => props.editActions.setEdited({ ...editedGrandNorm, name })}
      />
      {!userPermissions && <p className="color-red">Your role doesn&apos;t have necessary rights</p>}
    </Editor>
  );
};

const mapStateToProps = (state: State.Root) => ({
  editedGrandNorm: state.editView.editedObject,
  storedGrandNorm: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | PdApiActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditGrandNormCommon);
